import { useState, useRef, useEffect } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { inputVariants } from '@/components/ui/input'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { Select } from '@/components/ui/select'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function parseYearMonth(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]) - 1 }
}

function formatYearMonth(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

function formatDisplay(value: string): string {
  const parsed = parseYearMonth(value)
  if (!parsed) return ''
  return `${MONTH_NAMES[parsed.month]} ${parsed.year}`
}

export function MonthPicker({
  value,
  onChange,
  minYear = 1970,
  maxYear = 2100,
  disabled = false,
}: {
  value: string
  onChange: (value: string) => void
  minYear?: number
  maxYear?: number
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const parsed = parseYearMonth(value)
  const today = new Date()
  const year = parsed?.year ?? today.getFullYear()
  const month = parsed?.month ?? today.getMonth()

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  const years: number[] = []
  for (let y = minYear; y <= maxYear; y++) years.push(y)

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`${inputVariants()} flex items-center justify-between gap-2 text-left`}
      >
        <span className={value ? 'text-[var(--fl-text-1)]' : 'text-[var(--fl-text-2)]'}>
          {value ? formatDisplay(value) : 'Select month'}
        </span>
        <CalendarIcon size={16} className="text-[var(--fl-text-2)] shrink-0" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose month"
          className={`${MENU_SURFACE} absolute z-20 mt-1 w-64 p-3`}
        >
          <div className="flex items-center gap-1.5">
            <div className="flex-1">
              <Select
                aria-label="Month"
                size="sm"
                value={String(month)}
                onChange={(v) => onChange(formatYearMonth(year, Number(v)))}
                className="w-full"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-24 shrink-0">
              <Select
                aria-label="Year"
                size="sm"
                value={String(year)}
                onChange={(v) => onChange(formatYearMonth(Number(v), month))}
                className="w-full"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
